import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CircleCheck } from 'lucide-react'
import { Button } from '@/src/renderer/components/ui/Button'
import { SettingsRow, SettingsSection } from '../../primitives'
import { TodoistImportView } from './TodoistImportView'
import { ExportView, type ExportFormat } from './ExportView'
import type { PendingPreferencesAction } from '../../usePreferencesNavigation'
import './ImportExportPage.scss'

type View = { kind: 'todoist' } | { kind: 'export'; format: ExportFormat } | null

interface ImportExportPageProps {
  /** Export gibt es erst am Desktop (Entscheidung 3); im Web steht nur der Import. */
  platform: 'electron' | 'web'
  /** Vom Menü: gleich den Todoist-Import oder einen Export öffnen. */
  action?: PendingPreferencesAction | null
}

/**
 * **Profil & Daten › Import & Export** (#158, #252). Eine schlanke Übersicht: Import aus anderen Apps
 * (heute Todoist) und Export für andere Apps (CSV, Markdown); jeder Ablauf öffnet eine Unterseite.
 *
 * „Import“ heißt nur: aus anderen Apps herein; „Export“ nur: hinaus für andere Apps, nicht wieder
 * einlesbar. HappyTask ↔ HappyTask heißt „Backup“ und liegt auf der eigenen Seite.
 */
export function ImportExportPage({ platform, action }: ImportExportPageProps) {
  const { t } = useTranslation()
  const [view, setView] = useState<View>(null)
  const [exported, setExported] = useState<string | null>(null)

  // „Ablage › Importieren / Exportieren“ landet direkt im Ablauf, nicht auf der Übersicht.
  useEffect(() => {
    if (!action) return
    setExported(null)
    if (action.action === 'import-todoist') setView({ kind: 'todoist' })
    if (platform === 'electron' && action.action === 'export-csv') setView({ kind: 'export', format: 'csv' })
    if (platform === 'electron' && action.action === 'export-markdown') setView({ kind: 'export', format: 'markdown' })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- je Aktion genau einmal
  }, [action?.id])

  const openExport = (format: ExportFormat) => {
    setExported(null)
    setView({ kind: 'export', format })
  }

  return (
    <div className="import-export-page">
      <div hidden={view !== null}>
        <SettingsSection title={t('preferences.import.sectionTitle')} variant="card">
          <SettingsRow
            label={t('preferences.import.title')}
            description={t('preferences.import.description')}
            control={(
              <Button variant="outline" onClick={() => { setExported(null); setView({ kind: 'todoist' }) }}>
                {t('preferences.import.todoist.action')}
              </Button>
            )}
          />
        </SettingsSection>
        {platform === 'electron' && (
          <SettingsSection title={t('preferences.export.sectionTitle')} variant="card">
            <SettingsRow
              label={t('preferences.export.title')}
              description={(
                <>
                  {t('preferences.export.description')}
                  <span role="status" aria-live="polite" className="import-export-page__status">
                    {exported && (
                      <span className="import-export-page__done">
                        <CircleCheck size={14} aria-hidden="true" />
                        {t('preferences.export.done', { name: exported })}
                      </span>
                    )}
                  </span>
                </>
              )}
              control={(
                <div className="import-export-page__actions">
                  <Button variant="outline" onClick={() => openExport('csv')}>{t('preferences.export.csvAction')}</Button>
                  <Button variant="outline" onClick={() => openExport('markdown')}>{t('preferences.export.markdownAction')}</Button>
                </div>
              )}
            />
          </SettingsSection>
        )}
      </div>
      {view?.kind === 'todoist' && <TodoistImportView onBack={() => setView(null)} />}
      {view?.kind === 'export' && (
        <ExportView
          // Anderes Format, andere Unterseite — ein laufender Export der vorigen meldet sich dann nicht mehr.
          key={view.format}
          format={view.format}
          onBack={() => setView(null)}
          onExported={name => {
            setExported(name)
            setView(null)
          }}
        />
      )}
    </div>
  )
}
